import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { PreviewButton, canPreview, openPreview } from '@/components/PreviewButton'
import { AGENT_COMMANDS, type ChatCommand } from '@/lib/chatCommands'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowDownToLine,
  Bookmark,
  Check,
  ChevronRight,
  CircleHelp,
  Eye,
  FileText,
  Globe,
  MessageCircle,
  Pencil,
  Search,
  Square,
  Terminal,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { api, engine, mutate } from '@/runtime'
import { KindError } from '@/kinds/kind'
import { Button, CHIP_CLASS, Failed, Field, SectionTitle, Sheet, Tag, cx } from '@/components/ui'
import { MdLite } from '@/lib/mdlite'
import { RunPictures } from '@/components/RunPictures'
import { pictureSendError, runSendVia } from '@/lib/runSend'
import { PrimeComposer } from '@/components/PrimeComposer'
import { useAllProjects, useAllRuns, usePeople, useRunsForTask } from '@/db/hooks'
import { useStream, type LiveLine } from '@/state/stream'
import { useKeyboardUp } from '@/hooks/useVisualViewport'
import { useFollowScroll } from '@/lib/useFollowScroll'
import { useScope } from '@/state/scope'
import { ALL } from '@/kinds/workspace/schema'
import type { TaskResource } from '@/kinds/task/apply'
import type { ProjectResource } from '@/kinds/project/apply'
import type { AgentRunResource } from './apply'
import { isOver, type RunMessage } from './schema'
import { stepOf, stepSummary, type Step, type StepKind } from './toolSteps'
import { matchLines, type Call } from './transcript'
import { useTranscript } from './useTranscript'
import { Thinking, ToolCallDetail } from '@/components/ToolCall'
import { artifactNoteLine, parseArtifactNote, splitArtifactNote } from './artifactNote'
import { appLineOf, newsParts, type AppLine } from './news'
import { RunShots } from '@/components/Shots'
import { checkOf, phaseTone, phaseWords } from '@/lib/waiting'
import { RunTag } from '@/components/RunStatus'

// The software factory in the app (docs/factory.md): an agent session is a
// long-lived thread you talk to, like a Claude Code session. Read from its
// AgentRun mirror (the newest 200 lines) plus whatever the factory has streamed
// since; what you say, stopping it and ending it go through the kind's actions.

// a phase in words and its tone live with the waiting model (lib/waiting.ts), which
// every agent chip reads; they are passed on from here for the older imports
export { phaseTone, phaseWords }

export const repoName = (url: string) => url.replace(/^https?:\/\/[^/]+\//, '')

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * Whether the factory is on, and alfieOS's own repo (the default for a new
 * agent); `known` once the server has said (off until then).
 */
export const useFactory = (): { on: boolean; repo: string; known: boolean } => {
  const [f, setF] = useState({ on: false, repo: '', known: false })
  useEffect(() => {
    api
      .config()
      .then((c) => setF({ on: !!c.factory, repo: c.factory_repo ?? '', known: true }))
      .catch(() => setF({ on: false, repo: '', known: true }))
  }, [])
  return f
}

export const useFactoryEnabled = (): boolean => useFactory().on

const act = async (r: AgentRunResource, action: string, args: unknown, onError: (m: string) => void) => {
  try {
    await mutate.action(r.id, action, args)
    return true
  } catch (e) {
    onError(e instanceof KindError ? e.message : (e as Error).message)
    return false
  }
}

/** The session's phase: the streamed one when it is newer than the mirror. */
export const useRunPhase = (run: AgentRunResource) => {
  const live = useStream((s) => (run.spec.name ? s.runPhase[run.spec.name] : undefined))
  if (!live || isOver(run.spec.status)) return run.spec.status
  return Date.parse(run.spec.updated_at || '0') > live.at ? run.spec.status : live.phase
}

/** The conversation: the mirror's lines, then any streamed line it does not have yet. */
const useConversation = (run: AgentRunResource): (RunMessage | LiveLine)[] => {
  const name = run.spec.name ?? ''
  const stored = useMemo(() => run.spec.messages ?? [], [run.spec.messages])
  const lastStored = stored.length ? stored[stored.length - 1]!.seq : 0
  const live = useStream((s) => s.runLines[name])
  useEffect(() => {
    if (name && lastStored) useStream.getState().settleRun(name, lastStored)
  }, [name, lastStored])
  // on opening, fill in what the mirror does not carry yet (a missed webhook, a
  // turn that ended a moment ago); offline, the mirror is all there is
  useEffect(() => {
    if (!name || run.spec.source.provider !== 'factory') return
    api
      .factoryConversation(name, lastStored)
      .then(({ entries, phase }) => {
        const note = useStream.getState().note
        for (const entry of entries) note({ session: '', run: { name, entry } })
        if (phase) note({ session: '', run: { name, phase } })
      })
      .catch(() => {})
    // once per thread opened; live lines take it from there
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])
  return useMemo(
    () => [...stored, ...(live ?? []).filter((l) => l.seq > lastStored)],
    [stored, live, lastStored],
  )
}

// the factory's news, a fired watch and a member's Prime are not the owner's words (news.ts)
export { NEWS_PREFIX, WATCH_PREFIX } from './news'

/** How the app starts the lead (api/lead.go); shown as a note, not as your words. */
const LEAD_HELLO = 'You are starting up. Read CLAUDE.md here'

/** Whether a run is Prime's own session (the lead, Prime on Claude Code), not a coding agent. */
export const isLead = (r: AgentRunResource) => r.spec.template === 'prime'

/**
 * The factory's news in Prime's thread: the agent it is about (or the factory,
 * for a build) and its first line, and a way to the agent. Never the framing.
 */
const NewsCard = ({ body }: { body: string }) => {
  const { title, id, first } = newsParts(body)
  const inner = (
    <>
      <span className="font-semibold block truncate">{title ?? 'Software factory'}</span>
      <span className="block text-xs text-muted mt-0.5">{first}</span>
    </>
  )
  return (
    <div className="my-3" data-testid="run-news">
      {id ? (
        <Link
          to="/agents/run/$id"
          params={{ id }}
          className="block rounded-[var(--radius-card)] border border-line bg-panel/60 px-3 py-2 text-sm hover:bg-raised"
        >
          {inner}
        </Link>
      ) : (
        <div className="rounded-[var(--radius-card)] border border-line bg-panel/60 px-3 py-2 text-sm">
          {inner}
        </div>
      )}
    </div>
  )
}

type Block =
  | {
      kind: 'user' | 'assistant' | 'note'
      seq: number
      text: string
      artifactNote?: string
      pictures?: string[]
    }
  | { kind: 'tools'; seq: number; lines: string[]; ats: (string | undefined)[] }

/** Consecutive tool lines fold into one block, the way a terminal session reads. */
const toBlocks = (lines: (RunMessage | LiveLine)[]): Block[] => {
  const out: Block[] = []
  for (const l of lines) {
    const last = out[out.length - 1]
    if (l.role === 'tool') {
      if (last?.kind === 'tools') {
        last.lines.push(l.text)
        last.ats.push(l.at)
      } else out.push({ kind: 'tools', seq: l.seq, lines: [l.text], ats: [l.at] })
    } else if (l.role === 'user' || l.role === 'assistant' || l.role === 'note') {
      if (l.role === 'user') {
        // what the app added for Prime (an artifact edited) is shown apart, never as the owner's words
        const { words, note } = splitArtifactNote(l.text)
        if (note) {
          out.push({ kind: l.role, seq: l.seq, text: words, artifactNote: note, pictures: l.pictures })
          continue
        }
      }
      out.push({ kind: l.role, seq: l.seq, text: l.text, pictures: l.pictures })
    }
  }
  return out
}

const STEP_ICON: Record<StepKind, LucideIcon> = {
  run: Terminal,
  read: FileText,
  edit: Pencil,
  search: Search,
  web: Globe,
  talk: MessageCircle,
  ask: CircleHelp,
  do: Check,
  look: Eye,
  plan: Bookmark,
  other: Wrench,
}

const StepDot = ({ kind, live }: { kind: StepKind; live?: boolean }) => {
  const Icon = STEP_ICON[kind]
  return (
    <span
      className={cx(
        'h-[22px] w-[22px] rounded-full shrink-0 flex items-center justify-center border',
        live ? 'border-accent text-accent bg-accent-soft' : 'border-line bg-panel text-muted',
      )}
      aria-hidden
    >
      <Icon size={12} strokeWidth={2} />
    </span>
  )
}

/** What opening a step can show: the stream's calls paired with the thread's tool lines. */
interface Detail {
  /** one per tool line in the thread, in order */
  calls: (Call | undefined)[]
  loading: boolean
  error?: string
  /** a read has been made (a transcript, even without this call) */
  read: boolean
  want: () => void
  again: () => void
}

const StepRow = ({
  step,
  live,
  call,
  detail,
  at,
}: {
  step: Step
  live?: boolean
  call?: Call
  detail: Detail
  at?: string
}) => {
  const [open, setOpen] = useState(false)
  return (
    <li className="relative min-w-0" data-testid="run-step">
      <button
        type="button"
        onClick={() => {
          detail.want()
          setOpen((v) => !v)
        }}
        aria-expanded={open}
        className="flex w-full items-start gap-2.5 min-w-0 text-left"
      >
        <StepDot kind={step.kind} live={live} />
        <span className="min-w-0 flex-1 pt-[2px]">
          <span className={cx('text-[13px] leading-snug', live ? 'text-fg' : 'text-fg-2')}>{step.label}</span>
          {step.detail && (
            <span
              className={cx(
                'block font-mono text-[11.5px] leading-snug text-faint break-all',
                !open && 'line-clamp-2',
              )}
            >
              {step.detail}
            </span>
          )}
        </span>
        {call?.error && <span className="shrink-0 pt-[3px] text-[11.5px] text-rose-300">Failed</span>}
        <ChevronRight
          size={13}
          className={cx('mt-[5px] shrink-0 text-faint transition-transform', open && 'rotate-90')}
          aria-hidden
        />
      </button>
      {open && (
        <div className="ml-[32px] mt-1.5 mb-1">
          {detail.loading && !detail.read ? (
            <p className="text-[11.5px] text-faint">Reading the transcript…</p>
          ) : detail.error && !detail.read ? (
            <Failed what="this step's detail" reason={detail.error} onRetry={detail.again} compact />
          ) : (
            <ToolCallDetail
              call={call && { ...call, at: call.at ?? at }}
              pending={live}
              note="The transcript has no matching call for this step, so only its line is known."
            />
          )}
        </div>
      )}
    </li>
  )
}

/**
 * A run of tool calls, one line that never changes height: what was done (the
 * distinct steps, in order) behind their icons, or, while the session works on
 * it, the newest step. Open, it is a timeline of every step, and each step opens
 * once more to its full input and result (ToolCallDetail).
 */
const Tools = ({
  lines,
  base,
  stamps,
  live,
  detail,
}: {
  lines: string[]
  /** index of this run's first line among all the thread's tool lines */
  base: number
  stamps: (string | undefined)[]
  live: boolean
  detail: Detail
}) => {
  const [open, setOpen] = useState(false)
  const steps = useMemo(() => lines.map(stepOf), [lines])
  const { labels, more } = stepSummary(steps)
  const kinds = [...new Set(steps.map((s) => s.kind))].slice(0, 4)
  const newest = steps[steps.length - 1]
  return (
    <div className="my-2" data-testid="run-tools">
      <button
        type="button"
        onClick={() => {
          detail.want()
          setOpen((v) => !v)
        }}
        aria-expanded={open}
        className="flex items-center gap-2.5 w-full min-w-0 min-h-8 text-left text-[12.5px] text-muted hover:text-fg"
      >
        <span className="flex shrink-0">
          {live && newest ? (
            <StepDot kind={newest.kind} live />
          ) : (
            kinds.map((k, i) => (
              <span key={k} className={cx('rounded-full ring-2 ring-ink', i > 0 && '-ml-1.5')}>
                <StepDot kind={k} />
              </span>
            ))
          )}
        </span>
        <span className="truncate min-w-0 flex-1">
          {live && newest ? (
            <>
              <span className="text-fg">{newest.label}</span>
              {newest.detail && <span className="font-mono text-faint"> {newest.detail}</span>}
            </>
          ) : (
            <>
              {labels.join(', ')}
              {more > 0 && <span className="text-faint"> and {more} more</span>}
            </>
          )}
        </span>
        <span className="shrink-0 text-faint tabular-nums">
          {steps.length} {steps.length === 1 ? 'step' : 'steps'}
        </span>
        <ChevronRight
          size={13}
          className={cx('shrink-0 text-faint transition-transform', open && 'rotate-90')}
          aria-hidden
        />
      </button>
      {open && (
        <ol className="relative mt-1.5 flex flex-col gap-2 before:absolute before:left-[10.5px] before:top-3 before:bottom-3 before:w-px before:bg-line">
          {steps.map((st, i) => (
            <StepRow
              key={i}
              step={st}
              live={live && i === steps.length - 1}
              call={detail.calls[base + i]}
              at={stamps[i]}
              detail={detail}
            />
          ))}
        </ol>
      )}
    </div>
  )
}

/** A session's pull request on Forgejo: its own URL, else made from the repo and number. */
export const prUrlOf = (run: AgentRunResource) =>
  run.spec.pr_url ||
  (run.spec.pr_number && run.spec.repo_url
    ? `${run.spec.repo_url.replace(/\.git$/, '')}/pulls/${run.spec.pr_number}`
    : '')

/**
 * Under a stopped agent's thread: why it stopped, and the two ways on from
 * there (Prime looks at it, or a fresh agent on the same brief), so a failure
 * is never a dead end.
 */
const Stopped = ({ run }: { run: AgentRunResource }) => {
  const navigate = useNavigate()
  const [again, setAgain] = useState(false)
  const [error, setError] = useState('')
  const ask = async () => {
    setError('')
    try {
      await api.factoryReview(run.id, run.spec.started_by)
      void navigate({ to: '/agents' })
    } catch (e) {
      setError(navigator.onLine ? (e as Error).message || 'Prime could not be asked' : 'You are offline')
    }
  }
  return (
    <div className="card px-4 py-3" data-testid="run-stopped">
      <p className="text-[15px] font-semibold">It stopped</p>
      <p className="text-sm text-muted mt-0.5">
        {run.spec.message ? `${run.spec.message.replace(/\.$/, '')}.` : 'It could not carry on.'} Its thread
        stays here to read.
      </p>
      <div className="flex flex-wrap gap-2 mt-2.5">
        <Button variant="primary" onClick={() => void ask()} data-testid="run-stopped-ask">
          Ask Prime to look
        </Button>
        <Button variant="chip" onClick={() => setAgain(true)} data-testid="run-stopped-again">
          Start again
        </Button>
      </div>
      {error && <p className="text-xs text-muted mt-1.5">{error}</p>}
      {again && <NewAgentSheet again={run} onClose={() => setAgain(false)} />}
    </div>
  )
}

/** A session's pull request and the agent's account of it, pinned above the thread. */
export const PRCard = ({ run, onReview }: { run: AgentRunResource; onReview?: () => void }) => {
  const report = run.spec.report
  // pinned whenever there is a pull request, report or not: a failed check is when it matters most
  if (!run.spec.pr_number && !run.spec.pr_url && !report) return null
  const state = run.spec.pr_state
  const check = run.spec.pr_number && (state ?? 'open') === 'open' ? checkOf(run) : undefined
  const failed = check?.state === 'failed' || check?.state === 'timed out'
  const prUrl = prUrlOf(run)
  return (
    <article className="card px-4 py-3 mb-4" data-testid="run-pr">
      <div className="flex flex-wrap items-center gap-1.5 mb-1">
        {run.spec.pr_number ? (
          <Tag tone={state === 'merged' ? 'done' : state === 'closed' ? 'muted' : 'accent'}>
            PR #{run.spec.pr_number} {state ?? 'open'}
          </Tag>
        ) : null}
        {failed && <Tag tone="warn">{check?.state === 'timed out' ? 'Check timed out' : 'Check failed'}</Tag>}
        {check?.state === 'passed' && <Tag tone="done">Check passed</Tag>}
        {check?.state === 'running' && <Tag tone="running">Checking</Tag>}
        {report?.risk && <Tag>{capital(report.risk)} risk</Tag>}
        {report?.confidence && <Tag>{capital(report.confidence)} confidence</Tag>}
      </div>
      {report?.headline && <h3 className="font-semibold leading-snug">{report.headline}</h3>}
      {failed && (
        <p className="text-sm text-fg-2 mt-1" data-testid="run-pr-check">
          {check?.detail ? `The check failed: ${check.detail}.` : 'The check did not pass.'}
        </p>
      )}
      {report?.cause && <p className="text-sm text-muted mt-1 leading-relaxed">{report.cause}</p>}
      {!!report?.change?.length && (
        <details className="mt-2">
          <summary className="cursor-pointer select-none text-[13px] font-semibold text-muted py-1">
            {report.change.length === 1 ? '1 file' : `${report.change.length} files`}
          </summary>
          <ul className="group-list mt-1">
            {report.change.map((c) => (
              <li key={c.file} className="px-3 py-1.5">
                <div className="font-mono text-xs break-all">{c.file}</div>
                <div className="text-sm text-muted">{c.why}</div>
              </li>
            ))}
          </ul>
        </details>
      )}
      {!!run.spec.shots?.length && (
        <details className="mt-2" data-testid="run-pr-shots">
          <summary className="cursor-pointer select-none text-[13px] font-semibold text-muted py-1">
            Before and after
          </summary>
          <div className="mt-2">
            <RunShots run={run} narrow />
          </div>
        </details>
      )}
      <div className="flex flex-wrap gap-1.5 mt-2">
        <PreviewButton run={run} />
        {onReview && run.spec.pr_number ? (
          <Button variant="chip" onClick={onReview} data-testid="run-review-pr">
            Review
          </Button>
        ) : null}
        {prUrl && (
          <a href={prUrl} target="_blank" rel="noreferrer" className={CHIP_CLASS}>
            On Forgejo ↗
          </a>
        )}
      </div>
    </article>
  )
}

/** A member's Prime writing to this one: who it is from and what it says, never the framing. */
const MemberLine = ({ line }: { line: AppLine | undefined }) =>
  line?.kind === 'member' ? (
    <div className="my-3" data-testid="run-member-prime">
      <div className="rounded-[var(--radius-card)] border border-line bg-panel/60 px-3 py-2 text-sm">
        <span className="font-semibold block">From {line.who}'s Prime</span>
        <span className="block text-muted mt-0.5 whitespace-pre-wrap">{line.body}</span>
      </div>
    </div>
  ) : null

/** Under the owner's message: which artifacts Prime was told changed, in a line (the full sentence is Prime's). */
const ArtifactNoteLine = ({ note }: { note: string }) => {
  const people = usePeople()
  const me = people.find((p) => p.spec.me && !p.deleted_at)
  const line = artifactNoteLine(parseArtifactNote(note), me?.spec.name.split(/\s+/)[0] ?? '')
  if (!line) return null
  return (
    <p className="mt-1 max-w-[85%] text-right text-xs text-faint" data-testid="run-artifact-note">
      Prime was told {line}
    </p>
  )
}

/** One agent session as a thread: what was said and done, and the box to say more. */
export const SessionThread = ({
  run,
  header,
  cards,
  draft,
}: {
  run: AgentRunResource
  header?: ReactNode
  /** Prime's cards waiting for the owner, shown under the thread */
  cards?: ReactNode
  /** words handed over for the box, unsent (an Ask Prime made while Prime was not running) */
  draft?: string
}) => {
  const phase = useRunPhase(run)
  const keyboardUp = useKeyboardUp()
  const lines = useConversation(run)
  const blocks = useMemo(() => toBlocks(lines), [lines])
  // every tool line in the thread, in order: opening any step reads the transcript once and
  // pairs these with its calls (kinds/agentrun/transcript.ts), reading again as lines arrive
  const toolLines = useMemo(() => blocks.flatMap((b) => (b.kind === 'tools' ? b.lines : [])), [blocks])
  const [wantDetail, setWantDetail] = useState(false)
  const tx = useTranscript(run.spec.name ?? '', wantDetail, toolLines.length)
  const detail: Detail = useMemo(
    () => ({
      calls: tx.transcript ? matchLines(toolLines, tx.transcript.calls) : [],
      loading: tx.loading,
      error: tx.error,
      read: !!tx.transcript,
      want: () => setWantDetail(true),
      again: tx.again,
    }),
    [tx.transcript, tx.loading, tx.error, tx.again, toolLines],
  )
  const bases = useMemo(
    () =>
      blocks.map((_, i) =>
        blocks.slice(0, i).reduce((n, b) => (b.kind === 'tools' ? n + b.lines.length : n), 0),
      ),
    [blocks],
  )
  const pending = (run.status.writebacks ?? []).filter((w) => w.action === 'say')
  const [error, setError] = useState('')
  const over = isOver(phase)
  const working = phase === 'Working'
  const content = useRef<HTMLDivElement>(null)
  const thread = useRef<HTMLDivElement>(null)
  const composer = useRef<HTMLDivElement>(null)
  // the composer grows with what is typed (and a notice above it): the room kept under the
  // thread follows its height, so the newest line is never left behind it
  useLayoutEffect(() => {
    const el = composer.current
    const root = thread.current
    if (!el || !root) return
    const set = () => root.style.setProperty('--composer-h', `${Math.ceil(el.getBoundingClientRect().height)}px`)
    set()
    const ro = new ResizeObserver(set)
    ro.observe(el)
    return () => ro.disconnect()
  }, [over])

  // follow the thread by its height (see useFollowScroll), never while typing on a phone
  const follow = useFollowScroll(content, keyboardUp)
  const navigate = useNavigate()
  // the thread's slash commands (lib/chatCommands AGENT_COMMANDS): a prompt is said to
  // the agent; the rest are the session's own controls, the same as its ⋯ menu
  const commands = AGENT_COMMANDS.filter(
    (c) =>
      (c.act !== 'preview' || canPreview(run)) &&
      (c.act !== 'pr' || !!run.spec.pr_number) &&
      (c.act !== 'prime' || !isLead(run)) &&
      (!isLead(run) || !c.prompt),
  )
  const runCommand = async (c: ChatCommand) => {
    setError('')
    if (c.prompt) {
      follow.resume()
      await act(run, 'say', { text: c.prompt }, setError)
      return
    }
    switch (c.act) {
      case 'stop':
        await act(run, 'interrupt', {}, setError)
        return
      case 'end':
        if (confirm('End this session? Its thread stays readable.')) await act(run, 'end', {}, setError)
        return
      case 'pr':
        void navigate({ to: '/dev/review/$id', params: { id: run.id } })
        return
      case 'preview':
        await openPreview(run.spec.name ?? '').catch((e: Error) => setError(e.message || 'It did not start'))
        return
      case 'prime':
        try {
          await api.factoryReview(run.id, run.spec.started_by)
          void navigate({ to: '/agents' })
        } catch (e) {
          setError((e as Error).message || 'Prime could not be asked')
        }
    }
  }

  return (
    // on a desk the thread fills the window, so its composer sits at the bottom however short it is
    <div
      className="flex flex-col min-h-[60svh] md:min-h-[calc(100dvh-3rem)]"
      data-testid="run-thread"
      ref={thread}
      data-run={run.spec.name}
    >
      {header}
      <div className="flex-1" ref={content}>
        {blocks.map((b, i) =>
          b.kind === 'tools' ? (
            <Tools
              key={b.seq}
              lines={b.lines}
              base={bases[i] ?? 0}
              stamps={b.ats}
              live={working && i === blocks.length - 1}
              detail={detail}
            />
          ) : b.kind !== 'assistant' && appLineOf(b.text)?.kind === 'news' ? (
            <NewsCard key={b.seq} body={appLineOf(b.text)?.body ?? ''} />
          ) : b.kind !== 'assistant' && appLineOf(b.text)?.kind === 'watch' ? (
            <p
              key={b.seq}
              className="my-3 text-xs text-muted rounded-[var(--radius-card)] border border-line bg-panel/60 px-3 py-2"
              data-testid="run-watch-fired"
            >
              Watch fired: {appLineOf(b.text)?.body}
            </p>
          ) : b.kind !== 'assistant' && appLineOf(b.text)?.kind === 'member' ? (
            <MemberLine key={b.seq} line={appLineOf(b.text)} />
          ) : b.kind === 'user' && isLead(run) && b.text.startsWith(LEAD_HELLO) ? (
            <p key={b.seq} className="text-xs text-faint text-center my-3">
              Prime started
            </p>
          ) : b.kind === 'user' ? (
            <div key={b.seq} className="my-3 flex flex-col items-end">
              {b.pictures?.length && run.spec.name ? (
                <div className="mb-1 max-w-[85%]">
                  <RunPictures run={run.spec.name} files={b.pictures} />
                </div>
              ) : null}
              {(b.text.trim() || !b.pictures?.length) && (
                <div
                  className="max-w-[85%] rounded-[var(--radius-bubble)] bg-raised px-3.5 py-2 text-[15px] whitespace-pre-wrap"
                  data-testid="run-user"
                >
                  {b.text}
                </div>
              )}
              {b.artifactNote && <ArtifactNoteLine note={b.artifactNote} />}
            </div>
          ) : b.kind === 'assistant' ? (
            <div key={b.seq}>
              {tx.transcript?.thoughtsBefore.get(b.text.trim()) && (
                <Thinking text={tx.transcript.thoughtsBefore.get(b.text.trim()) ?? ''} />
              )}
              <MdLite text={b.text} className="text-[15px] leading-relaxed my-2" />
            </div>
          ) : (
            <p key={b.seq} className="text-xs text-faint text-center my-3" data-testid="run-note">
              {b.text}
            </p>
          ),
        )}
        {pending.map((w) => (
          <div key={w.at} className="my-3 flex justify-end">
            <div className="max-w-[85%] rounded-[var(--radius-bubble)] bg-raised px-3.5 py-2 text-[15px] whitespace-pre-wrap opacity-60">
              {String((w.args as { text?: string } | undefined)?.text ?? '')}
            </div>
          </div>
        ))}
        {cards}
        {/* its height is kept whether or not the session is working, so the line coming and
            going moves nothing */}
        <div className="my-3 h-5 text-sm text-muted">
          {(working || phase === 'Starting' || phase === 'Pending') && (
            <p className="flex items-center gap-2 leading-5" data-testid="run-working">
              <span
                className="inline-block size-2 rounded-full bg-[var(--color-now)] animate-pulse"
                aria-hidden
              />
              {phaseWords(phase)}…
            </p>
          )}
        </div>

        {/* room to scroll the newest line above the composer (and the tab bar on a phone) */}
        <div
          className="h-[calc(max(9rem,var(--composer-h,0px)+5.5rem)+var(--player-h,0px))] md:h-6"
          aria-hidden
        />
      </div>

      {!keyboardUp && (
        <div
          className="fixed z-20 inset-x-3 bottom-[calc(var(--above-tabbar)+5.5rem)] md:bottom-24 flex items-center justify-end gap-2 pointer-events-none"
          // clear of a panel docked to the right (artifacts beside the lead's thread)
          style={{ right: 'calc(0.75rem + var(--dock-w, 0px))' }}
          data-testid="run-follow"
        >
          {/* only once scrolled up: a short thread has nothing below to go to */}
          {(follow.unseen || follow.away) && (
            <button
              type="button"
              onClick={follow.jump}
              className="card-in pointer-events-auto mx-auto rounded-full bg-panel border border-line px-3 min-h-9 text-sm"
              data-testid="run-newest"
            >
              Newest ↓
            </button>
          )}
          {follow.away && (
            <button
              type="button"
              onClick={follow.toggle}
              aria-pressed={follow.enabled}
              aria-label="Auto-scroll"
              title={follow.enabled ? 'Auto-scroll is on' : 'Auto-scroll is off'}
              className={cx(
                'pointer-events-auto absolute right-0 grid place-items-center size-9 rounded-full border bg-panel',
                follow.enabled ? 'border-accent/50 text-accent-text' : 'border-line text-faint',
              )}
              data-testid="run-autoscroll"
            >
              <ArrowDownToLine size={16} aria-hidden />
            </button>
          )}
        </div>
      )}

      {/* the composer: on a phone pinned above the tab bar (and the player), or above the
          keyboard while typing, as in Prime's thread; on a desk it follows the thread */}
      <div
        ref={composer}
        className="kb-follow fixed inset-x-3 z-20 bottom-[calc(var(--above-tabbar)+0.75rem)] md:sticky md:inset-auto md:bottom-3 md:mt-4"
        style={keyboardUp ? { bottom: 'calc(var(--vv-gap) + 0.5rem)' } : undefined}
      >
        {error && <p className="text-xs text-rose-300 mb-1">{error}</p>}
        {run.status.writeback_error && (
          <p className="text-xs text-muted mb-1">
            The factory didn't take it yet: {run.status.writeback_error}
          </p>
        )}
        {phase === 'Failed' ? (
          <Stopped run={run} />
        ) : over ? (
          <p className="text-sm text-muted text-center py-3">This session has ended.</p>
        ) : (
          <PrimeComposer
            // pictures for a coding agent and Prime's lead alike
            pictures
            compact
            onSend={async (m) => {
              setError('')
              follow.resume()
              if (runSendVia(m) === 'say') {
                if (!(await act(run, 'say', { text: m.text }, setError))) throw new Error('not sent')
                return
              }
              // pictures never ride an op: straight to the factory, online only; a refusal
              // (an ended session, a picture it will not take) leaves the draft where it is.
              // Words still waiting to reach the factory go first: a picture sent now
              // would overtake them.
              if (pending.length > 0) {
                setError('Waiting for your last message to reach the agent; send the picture once it has.')
                throw new Error('a message is still on its way')
              }
              try {
                await api.factorySend(run.spec.name ?? '', m.text, m.images)
              } catch (e) {
                setError(pictureSendError(e, navigator.onLine))
                throw e
              }
            }}
            onError={setError}
            commands={over ? undefined : commands}
            onCommand={(c) => void runCommand(c)}
            dictate
            placeholder={
              phase === 'Suspended'
                ? 'Say something to wake it…'
                : working
                  ? 'It will read this next…'
                  : 'Reply…'
            }
            label="Message"
            inputTestId="run-input"
            initial={draft}
            sendTestId="run-send"
            sendLabel="Send"
            beside={
              working ? (
                <Button
                  variant="quiet"
                  onClick={() => void act(run, 'interrupt', {}, setError)}
                  aria-label="Stop"
                  data-testid="run-stop"
                >
                  <Square size={15} aria-hidden />
                </Button>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  )
}

/** The repos you have worked in: projects' repos and the sessions' own. */
// alfieOS itself first, then each project's repos, then any an agent has used
const useKnownRepos = (): string[] => {
  const projects = useAllProjects()
  const runs = useAllRuns()
  const self = useFactory().repo
  return useMemo(() => {
    const seen = new Set<string>()
    for (const p of projects ?? []) for (const r of p.spec.repos ?? []) if (r) seen.add(repoName(r))
    for (const r of runs ?? []) if (r.spec.repo_url) seen.add(repoName(r.spec.repo_url))
    const rest = [...seen].filter((r) => r !== self).sort()
    return self ? [self, ...rest] : rest
  }, [projects, runs, self])
}

const startError = (e: unknown) =>
  !navigator.onLine ? 'You are offline. Starting an agent needs a connection.' : (e as Error).message

/** Start a coding agent: which repo, and what to say first. */
export const NewAgentSheet = ({
  onClose,
  task,
  project,
  session,
  again,
}: {
  onClose: () => void
  task?: TaskResource
  project?: ProjectResource
  /** the chat it was started from, which hears when the agent finishes */
  session?: string
  /** a stopped agent to start afresh: its repo and its first brief (a task's own session
   *  would be handed back, so the new one carries the title, not the task) */
  again?: AgentRunResource
}) => {
  const navigate = useNavigate()
  const known = useKnownRepos()
  // the session belongs where it was started: the project's workspace, else the current one
  const scope = useScope((s) => s.ws)
  const workspace = project?.labels.ws ?? (scope === ALL ? undefined : scope)
  const projectRepos = (project?.spec.repos ?? []).filter(Boolean)
  // the default follows what is known (alfieOS's repo arrives with the config)
  // until something is typed
  const [typed, setRepo] = useState<string | null>(null)
  const againRepo = again?.spec.repo_url.replace(/^https?:\/\/[^/]+\//, '').replace(/\.git$/, '')
  const repo = typed ?? againRepo ?? projectRepos[0] ?? known[0] ?? ''
  const [prompt, setPrompt] = useState(() =>
    task
      ? [task.spec.title, task.spec.notes?.trim()].filter(Boolean).join('\n\n')
      : again
        ? ((again.spec.messages ?? []).find((m) => m.role === 'user')?.text ?? '')
        : '',
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const start = async () => {
    setBusy(true)
    setError('')
    try {
      const made = await api.factoryHandOff({
        repo: repo.trim(),
        prompt: prompt.trim(),
        ...(task ? { task_id: task.id, title: task.spec.title } : {}),
        ...(again && !task ? { title: again.spec.title || undefined } : {}),
        ...(workspace ? { workspace } : {}),
        ...(project ? { project_id: project.id } : {}),
        ...(session ? { session } : {}),
      })
      await engine.sync()
      onClose()
      void navigate({ to: '/agents/run/$id', params: { id: made.id } })
    } catch (e) {
      setError(startError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title={task ? 'Hand to agent' : again ? 'Start it again' : 'New coding agent'}
      footer={
        <Button
          variant="primary"
          big
          className="w-full"
          onClick={() => void start()}
          disabled={busy || !repo.trim() || !prompt.trim()}
          data-testid="new-agent-start"
        >
          {busy ? 'Starting…' : 'Start'}
        </Button>
      }
    >
      <Field label="Repository" hint="owner/repo on Forgejo; the factory bot needs write access to it">
        <input
          value={repo}
          onChange={(e) => setRepo(e.target.value)}
          list="known-repos"
          placeholder="alfiemills/alfieos"
          className="w-full"
          data-testid="new-agent-repo"
        />
        <datalist id="known-repos">
          {known.map((r) => (
            <option key={r} value={r} />
          ))}
        </datalist>
      </Field>
      <Field
        label="First message"
        hint="It works on its own branch in a sandbox; talk to it as long as you like."
      >
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="w-full min-h-40"
          data-testid="new-agent-prompt"
        />
      </Field>
      {error && <p className="text-sm text-rose-300">{error}</p>}
    </Sheet>
  )
}

/** The sessions started from one task, on its page, each opening its thread. */
export const TaskRuns = ({ taskId }: { taskId: string }) => {
  const runs = useRunsForTask(taskId)
  const navigate = useNavigate()
  if (!runs.length) return null
  return (
    <section className="mb-6" data-testid="task-runs">
      <SectionTitle>Agent</SectionTitle>
      <ul className="group-list">
        {runs.slice(0, 3).map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => void navigate({ to: '/agents/run/$id', params: { id: r.id } })}
              className="w-full text-left px-3 py-2.5 min-h-12"
            >
              <div className="leading-5">{r.spec.report?.headline || r.spec.title}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                <RunTag run={r} />
                {r.spec.pr_number ? <Tag>PR #{r.spec.pr_number}</Tag> : null}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Hand a task to an agent: the new-agent sheet with the task's words to start from. */
export const HandToAgentSheet = ({
  task,
  project,
  onClose,
}: {
  task: TaskResource
  project?: ProjectResource
  onClose: () => void
}) => <NewAgentSheet task={task} project={project} onClose={onClose} />
